import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, X } from 'lucide-react';
import type { ChatMessage } from '../hooks/useMultiplayer';
import { MAX_CHAT_MESSAGE_LENGTH } from '../constants';
import { useTranslation } from '../i18n';

interface ChatBoxProps {
  messages: ChatMessage[];
  onSend: (text: string) => void;
  playerName: string;
}

const ChatBox: React.FC<ChatBoxProps> = ({ messages, onSend, playerName }) => {
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // ── Touch drag state ────────────────────────────────────────────────────
  const [dragY, setDragY] = useState(0);
  const dragStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);

  // ── Auto-scroll to bottom on new messages ───────────────────────────────
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setUnreadCount(0);
    }
  }, [messages.length, isOpen]);

  // ── Track unread when closed ────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (!lastMsg.isMine) {
        setUnreadCount((prev) => prev + 1);
      }
    }
  }, [messages.length]);

  // ── Send message ────────────────────────────────────────────────────────
  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    onSend(input);
    setInput('');
    inputRef.current?.focus();
  }, [input, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Toggle ──────────────────────────────────────────────────────────────
  const toggle = () => {
    setIsOpen((prev) => {
      if (!prev) setUnreadCount(0);
      return !prev;
    });
    setDragY(0);
  };

  // ── Touch handlers for drag-to-close ────────────────────────────────────
  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartRef.current = e.touches[0].clientY;
    isDraggingRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartRef.current === null) return;
    const delta = e.touches[0].clientY - dragStartRef.current;
    if (delta > 0) {
      isDraggingRef.current = true;
      setDragY(delta);
    }
  };

  const handleTouchEnd = () => {
    if (dragY > 100) {
      // Dragged far enough -> close
      setIsOpen(false);
    }
    setDragY(0);
    dragStartRef.current = null;
    isDraggingRef.current = false;
  };

  // ── Format timestamp ────────────────────────────────────────────────────
  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* ── Toggle button (always visible) ─────────────────────────────── */}
      <button
        onClick={toggle}
        className="fixed bottom-4 right-4 z-40 w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-theme pixel-border flex items-center justify-center text-white text-sm lg:text-lg font-bold shadow-lg hover:scale-110 active:scale-95 transition-transform"
      >
        {isOpen
          ? <X className="w-4 h-4 lg:w-5 lg:h-5" strokeWidth={3} />
          : <MessageSquare className="w-4 h-4 lg:w-5 lg:h-5" strokeWidth={2.5} fill="currentColor" />}
        {!isOpen && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 lg:w-5 lg:h-5 bg-red-500 rounded-full text-[8px] lg:text-[10px] flex items-center justify-center font-bold text-white animate-bounce">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* ── Chat panel ─────────────────────────────────────────────────── */}
      <div
        ref={panelRef}
        className={`fixed z-30 transition-transform duration-300 ease-out
          ${/* Mobile: bottom sheet */ ''}
          bottom-0 right-0 left-0 lg:left-auto lg:bottom-4 lg:right-4
          ${/* Desktop: side panel */ ''}
          lg:w-80 lg:rounded-lg
          ${isOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-[120%]'}
        `}
        style={{
          transform: isOpen
            ? `translateY(${dragY}px)`
            : undefined,
          maxHeight: '60dvh',
        }}
      >
        <div className="flex flex-col bg-[#0d0a12] border-t-2 lg:border-2 border-theme/50 lg:rounded-lg overflow-hidden shadow-2xl"
          style={{ maxHeight: '60dvh' }}
        >
          {/* ── Header / drag handle ───────────────────────────────────── */}
          <div
            className="flex items-center justify-between px-3 py-2 bg-theme/10 border-b border-theme/20 cursor-grab active:cursor-grabbing shrink-0"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Mobile drag pill */}
            <div className="w-8 h-1 bg-gray-600 rounded-full mx-auto absolute left-1/2 -translate-x-1/2 top-1 lg:hidden" />

            <span className="text-[9px] lg:text-xs text-theme font-bold uppercase tracking-widest mt-1 lg:mt-0">
              {t.chat}
            </span>
            <span className="text-[8px] lg:text-[10px] text-gray-500">
              {t.msgCount(messages.length)}
            </span>
          </div>

          {/* ── Messages ───────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto p-2 lg:p-3 space-y-1.5 min-h-[120px] max-h-[40dvh] custom-scrollbar">
            {messages.length === 0 ? (
              <div className="text-center text-gray-400 text-[8px] lg:text-xs italic py-8 uppercase">
                {t.noMessages}
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex flex-col ${msg.isMine ? 'items-end' : 'items-start'}`}
                >
                  <div className="flex items-center gap-1 mb-0.5">
                    <span className={`text-[8px] lg:text-[10px] font-bold uppercase ${msg.isMine ? 'text-blue-400' : 'text-red-400'}`}>
                      {msg.isMine ? t.chatYou : msg.playerName}
                    </span>
                    <span className="text-[8px] lg:text-[8px] text-gray-400">
                      {formatTime(msg.timestamp)}
                    </span>
                  </div>
                  <div
                    className={`px-2 py-1 lg:px-3 lg:py-1.5 rounded-lg max-w-[85%] break-words text-[9px] lg:text-xs ${
                      msg.isMine
                        ? 'bg-blue-900/40 text-blue-200 border border-blue-800/50'
                        : 'bg-red-900/30 text-red-200 border border-red-800/50'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* ── Input ──────────────────────────────────────────────────── */}
          <div className="flex gap-1.5 p-2 lg:p-3 border-t border-theme/20 bg-black/40 shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={MAX_CHAT_MESSAGE_LENGTH}
              placeholder={t.typeMessage}
              className="flex-1 bg-black/60 text-white text-[9px] lg:text-xs px-2 py-1.5 lg:px-3 lg:py-2 rounded pixel-border border-gray-700 focus:border-theme focus:outline-none placeholder:text-gray-500 placeholder:uppercase"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="px-3 py-1.5 lg:px-4 lg:py-2 bg-theme text-black text-[8px] lg:text-xs font-bold uppercase pixel-border active:translate-y-0.5 transition-all disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed shrink-0"
            >
              {t.send}
            </button>
          </div>
        </div>
      </div>

      {/* ── Backdrop (mobile only, when open) ──────────────────────────── */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/30 lg:hidden"
          onClick={toggle}
        />
      )}
    </>
  );
};

export default ChatBox;
